import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { useAuth } from '@/contexts/AuthContext'
import { useBusiness } from '@/contexts/BusinessContext'
import { BookPriceModel, type BookPriceRow, type PriceBook } from '@/http/models/PosCatalog'
import { apiErrorMessage } from '@/lib/apiError'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { FilterSelect } from './FilterSelect'
import { usePosLookups } from './usePosLookups'

const LENGTH = 50

interface Props {
  book: PriceBook | null
  onClose: () => void
}

/** A price book's prices at one branch. Empty or 0 = no book price. */
export const BookPricesDialog = ({ book, onClose }: Props) => {
  const { can } = useAuth()
  const { formatCurrency } = useBusiness()
  const lookups = usePosLookups(!!book)
  const [branch, setBranch] = useState('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<BookPriceRow[]>([])
  const [count, setCount] = useState(0)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const editable = can('price_books:update')

  useEffect(() => {
    if (!branch && lookups.branches[0]) setBranch(lookups.branches[0]._id)
  }, [branch, lookups.branches])

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (!book || !branch) return
    let cancelled = false
    setLoading(true)
    BookPriceModel.getList(book._id, { branch_id: branch, search: query || undefined, page, length: LENGTH }).then((res) => {
      if (cancelled) return
      if (res.success && res.data) {
        setRows(res.data.result)
        setCount(res.data.count)
      } else toast.error(apiErrorMessage(res, 'Could not load the prices'))
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [book, branch, query, page])

  if (!book) return null

  const changed = Object.entries(edits)
  const save = async () => {
    setSaving(true)
    const res = await BookPriceModel.save(book._id, {
      branch_id: branch,
      prices: changed.map(([id, value]) => ({ product_variation_id: id, sale_price: Math.max(0, Number(value) || 0) })),
    })
    setSaving(false)
    if (!res.success) return toast.error(apiErrorMessage(res, 'Could not save the prices'))
    toast.success('Prices saved', { description: 'Devices get them with their next sync.' })
    setRows((list) => list.map((r) => (r._id in edits ? { ...r, book_price: Number(edits[r._id]) || null } : r)))
    setEdits({})
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{book.title} prices</DialogTitle>
          <DialogDescription>Leave empty to use the branch or regular price.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 sm:flex-row">
          <FilterSelect
            label="Branch"
            value={branch}
            onChange={(v) => {
              setBranch(v)
              setEdits({})
              setPage(1)
            }}
            allLabel="Choose a branch"
            options={lookups.branches.map((b) => ({ label: b.name, value: b._id }))}
          />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product or SKU" />
        </div>
        <div className="max-h-[50vh] overflow-y-auto rounded-md border border-border-subtle">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="hidden sm:table-cell">SKU</TableHead>
                <TableHead className="text-right">Regular</TableHead>
                <TableHead className="w-32 text-right">Book price</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-6 text-center"><Spinner /></TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">{branch ? 'No products found.' : 'Choose a branch.'}</TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>
                      <div className="font-medium text-ink">{r.product_name}</div>
                      {r.name && <div className="text-xs text-muted-foreground">{r.name}</div>}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{r.sku}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(r.regular_price ?? 0)}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        aria-label={`Book price for ${r.product_name}`}
                        inputMode="decimal"
                        disabled={!editable}
                        className="h-8 text-right tabular-nums"
                        value={edits[r._id] ?? (r.book_price ? String(r.book_price) : '')}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [r._id]: e.target.value.replace(/[^\d.]/g, '') }))}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <DialogFooter className="items-center gap-2 sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <span>Page {page} of {Math.max(1, Math.ceil(count / LENGTH))}</span>
            <Button variant="outline" size="sm" disabled={page * LENGTH >= count || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
          {editable && (
            <Button onClick={save} disabled={!changed.length || saving || !branch}>
              {saving && <Spinner />}
              Save {changed.length ? `(${changed.length})` : ''}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
